/**
 * Scrapers section shell — three-tab nav at top + Outlet for the
 * active sub-page (Diagnose / Onboard / Manage).
 *
 * Tab order matches what we agreed: action tabs first (Diagnose,
 * Onboard), reference tab (Manage = the existing scraper list)
 * last. Default landing on /scrapers redirects to last-used tab
 * via localStorage; first-time visitors land on Manage so the
 * existing muscle memory is preserved.
 */

import { Link, Outlet, useLocation } from '@tanstack/react-router';
import { cn } from '@/lib/cn';

const TABS: ReadonlyArray<{ to: string; label: string; description: string }> = [
  { to: '/scrapers/diagnose',  label: 'Diagnose',  description: 'Test any URL across every engine. Find out what works.' },
  { to: '/scrapers/remediate', label: 'Remediate', description: 'Diagnose blocked scrapers across candidate executors, switch executor in 1-click, open PR, and auto-merge on green CI.' },
  { to: '/scrapers/onboard',   label: 'Onboard',   description: 'Add a new scraping target end-to-end.' },
  { to: '/scrapers/manage',    label: 'Manage',    description: 'Per-source schedule + pause controls.' },
];

const LAST_TAB_KEY = 'otr.scrapers.lastTab';

/** Remember the user's tab choice so refreshing /scrapers/diagnose
 *  doesn't bounce them to Manage. Persisted on every nav. */
function rememberTab(path: string) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(LAST_TAB_KEY, path);
}

export default function ScrapersLayout() {
  const loc = useLocation();
  const active = TABS.find((t) => loc.pathname.startsWith(t.to));
  if (active) rememberTab(active.to);

  const tagline = active?.description
    ?? 'Per-source schedule + pause controls. Changes apply on the next cron tick.';

  return (
    <div className="space-y-4">
      <header className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <nav role="tablist" className="flex items-center gap-1 bg-slate-900/60 border border-slate-800 rounded-md p-0.5">
            {TABS.map((t) => {
              const isActive = loc.pathname.startsWith(t.to);
              return (
                <Link
                  key={t.to}
                  to={t.to}
                  role="tab"
                  aria-selected={isActive}
                  className={cn(
                    'px-3 py-1.5 text-xs font-medium rounded transition whitespace-nowrap',
                    isActive
                      ? 'bg-slate-800 text-white'
                      : 'text-slate-400 hover:text-white',
                  )}
                >
                  {t.label}
                </Link>
              );
            })}
          </nav>
          <p className="text-sm text-slate-500 mt-2">{tagline}</p>
        </div>
      </header>

      <Outlet />
    </div>
  );
}

/** Used by the router root-redirect to land on the last-used tab,
 *  defaulting to Manage on first visit. */
export function pickDefaultScrapersTab(): string {
  if (typeof window === 'undefined') return '/scrapers/manage';
  const saved = window.localStorage.getItem(LAST_TAB_KEY);
  if (saved && TABS.some((t) => t.to === saved)) return saved;
  return '/scrapers/manage';
}
